import { MULTI_VALUED_FIELDS } from './creativesFormattingUtils';

export const DimensionCategory = {
    GENERAL_DIMENSIONS: 'Creative Dimensions',
    AUDIO: 'Audio',
    TEXT: 'Text',
    STYLE: 'Basic Ad Style',
    ELEMENTS: 'Elements',
    CHARACTER: 'Character',
    HOOK: 'Hook',
    OTHER: 'Other',
};

export const DimensionCategoryToColor = {
    [DimensionCategory.GENERAL_DIMENSIONS]: 'var(--gray700)',
    [DimensionCategory.AUDIO]: 'var(--orange300)',
    [DimensionCategory.TEXT]: 'var(--orange400)',
    [DimensionCategory.STYLE]: 'var(--purple300)',
    [DimensionCategory.ELEMENTS]: 'var(--blue400)',
    [DimensionCategory.CHARACTER]: 'var(--pink200)',
    [DimensionCategory.HOOK]: 'var(--green300)',
    [DimensionCategory.OTHER]: 'var(--turquoise100)',
};

export const NO_TAGS_COLOR = 'var(--gray400)';

const creativeWithUpdatedMultiValued = (creative, dimensionName, updatedValues) => ({
    ...creative,
    multiValuedDimensions: { ...creative.multiValuedDimensions, [dimensionName]: updatedValues },
});

const filterByMultiValuedDimension = (creative, creativeField, filterValues) => {
    if (!filterValues) return creativeWithUpdatedMultiValued(creative, creativeField, []);
    if (!filterValues.length) return creative;
    const creativeFilteredValue =
        creative.multiValuedDimensions[creativeField]?.filter(filter => filterValues.includes(filter)) || [];

    return creativeFilteredValue.length
        ? creativeWithUpdatedMultiValued(creative, creativeField, creativeFilteredValue)
        : null;
};

const filterBySingleValuedDimension = (creative, creativeField, filterValues) => {
    if (!filterValues) {
        const dimensionConfig = Object.values(GeneralDimensionsConfig).find(
            config => config.creativeField === creativeField
        );
        if (dimensionConfig?.shouldShowOnCard)
            return { ...creative, [`show${creativeField.charAt(0).toUpperCase()}${creativeField.slice(1)}`]: false };
        return creative;
    }
    if (!filterValues.length) return creative;

    return filterValues.includes(creative[creativeField]) ? creative : null;
};

export const TYPE_FILTER = 'Creative Type';
export const OS_FILTER = 'OS';
export const CAMPAIGN_NAME_FILTER = 'Campaign Name';
export const COUNTRY_FILTER = 'Country';
export const ACCOUNT_NAME_FILTER = 'Account Name';
export const VIDEO_DURATION_RANGE_FILTER = 'Video Duration Range';
export const ORIENTATION_FILTER = 'Creative Orientation';

export const GeneralDimensionsConfig = {
    [TYPE_FILTER]: {
        isMultiValued: false,
        creativeField: 'type',
        isDefault: true,
        shouldShowOnCard: true,
        filterBy: (creative, filterValues) => filterBySingleValuedDimension(creative, 'type', filterValues),
        icon: 'photo-video',
    },
    [OS_FILTER]: {
        isMultiValued: true,
        creativeField: 'osList',
        isDefault: true,
        shouldShowOnCard: true,
        filterBy: (creative, filterValues) => filterByMultiValuedDimension(creative, 'osList', filterValues),
        icon: 'device-desktop',
    },
    [CAMPAIGN_NAME_FILTER]: {
        isMultiValued: true,
        creativeField: 'campaignNameList',
        isDefault: true,
        shouldShowOnCard: true,
        filterBy: (creative, filterValues) => filterByMultiValuedDimension(creative, 'campaignNameList', filterValues),
        icon: 'article',
    },
    [COUNTRY_FILTER]: {
        isMultiValued: true,
        creativeField: 'countryList',
        isDefault: false,
        shouldShowOnCard: true,
        filterBy: (creative, filterValues) => filterByMultiValuedDimension(creative, 'countryList', filterValues),
        icon: 'world-pin',
    },
    [ACCOUNT_NAME_FILTER]: {
        isMultiValued: true,
        creativeField: 'accountNameList',
        isDefault: false,
        shouldShowOnCard: true,
        filterBy: (creative, filterValues) => filterByMultiValuedDimension(creative, 'accountNameList', filterValues),
        icon: 'briefcase',
    },
    [ORIENTATION_FILTER]: {
        isMultiValued: false,
        creativeField: 'orientation',
        isDefault: false,
        shouldShowOnCard: false,
        filterBy: (creative, filterValues) => filterBySingleValuedDimension(creative, 'orientation', filterValues),
        icon: 'file-orientation',
    },
    [VIDEO_DURATION_RANGE_FILTER]: {
        isMultiValued: false,
        creativeField: 'videoDurationRange',
        shouldShowOnCard: false,
        isDefault: false,
        filterBy: (creative, filterValues) =>
            filterBySingleValuedDimension(creative, 'videoDurationRange', filterValues),
        icon: 'clock-play',
    },
};

export const ASYNC_FILTER_TO_FIELD_CONFIG = Object.entries(GeneralDimensionsConfig).reduce(
    (acc, [filterName, config]) => {
        if (!config.isMultiValued) return acc;
        const fieldConfig = MULTI_VALUED_FIELDS.find(field => field.aggregatedField === config.creativeField);
        if (fieldConfig) acc[filterName] = fieldConfig;
        return acc;
    },
    {}
);

export const isLazyGeneralDimension = filterName => !!ASYNC_FILTER_TO_FIELD_CONFIG[filterName]?.isLazy;

export const getActiveLazyDimensionFields = filterNames =>
    (filterNames || [])
        .filter(isLazyGeneralDimension)
        .map(filterName => ASYNC_FILTER_TO_FIELD_CONFIG[filterName].originalField);
